import {
  useGetOrganisationIdpConnections,
  useUpdateOrganisationIdpConnection,
} from '@/generated/http-clients/v3ApiComponents'
import {
  ERROR_TOAST_OPTION,
  LOADING_TOAST_OPTION,
  SUCCESS_TOAST_OPTION,
  showOrUpdateToast,
  toaster,
} from '@/hooks/useToast/toastUtils'
import { useQueryClient } from '@tanstack/react-query'
import get from 'lodash/get'
import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'

export const IDP_CONNECTIONS_STALE_TIME_IN_MILLISECONDS = 30_000

const UPDATE_IDP_CONNECTION_TOAST_ID =
  'org-features-update-idp-connection-toast'

export function useOrgIdpConnections(
  orgId: string,
  { enabled: isOpen }: { enabled: boolean },
) {
  const { t } = useTranslation('features')
  const queryClient = useQueryClient()

  const idpConnections = useGetOrganisationIdpConnections(
    { pathParams: { orgId } },
    {
      enabled: isOpen && Boolean(orgId),
      refetchOnWindowFocus: false,
      staleTime: IDP_CONNECTIONS_STALE_TIME_IN_MILLISECONDS,
      retry: 1,
    },
  )

  const updateOrgIdpConnectionRequest = useUpdateOrganisationIdpConnection({
    onMutate: () => {
      toaster.create({
        ...LOADING_TOAST_OPTION,
        id: UPDATE_IDP_CONNECTION_TOAST_ID,
        title: t('organization.features.updateOrgIdpConnection.loadingTitle'),
        description: t(
          'organization.features.updateOrgIdpConnection.loadingDescription',
        ),
      })
      return UPDATE_IDP_CONNECTION_TOAST_ID
    },
    onSuccess: (_data, variables, toastId: unknown) => {
      const toastContent = {
        ...SUCCESS_TOAST_OPTION,
        title: t('organization.features.updateOrgIdpConnection.successTitle'),
        description: t(
          'organization.features.updateOrgIdpConnection.successDescription',
        ),
      }
      showOrUpdateToast(String(toastId), toastContent)
      queryClient.invalidateQueries({
        queryKey: [
          'api',
          'v3',
          'orgs',
          variables.pathParams.orgId,
          'idpConnections',
        ],
      })
    },
    onError: (error, _context: unknown, toastId: unknown) => {
      const toastContent = {
        ...ERROR_TOAST_OPTION,
        title: t('organization.features.updateOrgIdpConnection.errorTitle'),
        description: String(
          get(error, 'stack.description') ||
            get(error, 'stack._embedded.errors[0].message') ||
            get(error, 'message') ||
            t('organization.features.updateOrgIdpConnection.errorDescription'),
        ),
      }
      showOrUpdateToast(String(toastId), toastContent)
    },
  })

  const updateOrgIdpConnection = useCallback(
    (connectionId: string, isEnabled: boolean) => {
      if (!orgId) {
        return Promise.reject(new Error('No valid organization ID selected'))
      }

      return updateOrgIdpConnectionRequest.mutateAsync({
        pathParams: { orgId, connectionId },
        body: { enabled: isEnabled },
      })
    },
    [orgId, updateOrgIdpConnectionRequest],
  )

  return { idpConnections, updateOrgIdpConnection }
}
